<style lang="less">
.l-summary {
  border-bottom: none;
}
</style>

<template>
  <FormItem :label="label" :class="{ 'ugo-hide-label': true }">
    <div class="contract-service-split">
      <BaseFlexTablePro
        style="border: 1px solid var(--border);"
        :columns="presetColumns"
        v-model="items"
        @input="handleSave"
        :model="formModel"
      ></BaseFlexTablePro>

      <template v-if="showSplitCheck">
        <i-row :gutter="10" class-name="l-bill l-summary" style="border-top: 1px solid var(--border);">
          <i-col :span="8">
            <div class="flex-wrap flex-left">
              已拆分金额
              <BaseAmount full :value="splitSum" style="font-weight: bold; margin-left: 12px;"></BaseAmount>
            </div>
          </i-col>
          <i-col :span="8">
            <div class="flex-wrap flex-left">
              合同总额
              <BaseAmount full :value="numericContractTotal" style="font-weight: bold; margin-left: 12px;"></BaseAmount>
            </div>
          </i-col>
          <i-col :span="8">
            <div class="flex-wrap flex-left">
              拆分比例
              <span style="font-weight: bold; margin-left: 12px;">{{ splitRatioDisplay }}</span>
            </div>
          </i-col>
        </i-row>
        <div style="margin-top: 6px;">
          <Alert :type="checkResult.type" size="small" style="padding: 3px 8px">{{ checkResult.message }}</Alert>
        </div>
      </template>
    </div>
  </FormItem>
</template>

<script>
import defaultValueMixin from '../mixins/default-value';
import composedMixin from '../mixins/composed';
import { cloneDeep } from 'lodash';
import valueDefMixin from '../mixins/value-def';

const BUSINESS_TYPES = ['全过程咨询', '项目管理', '房建监理', '市政监理', '造价咨询', 'BIM咨询', '招标代理', '对外合作', '其他'];
const SPLIT_TYPES = ['自营', '联合体', '外委'];

export default {
  mixins: [defaultValueMixin, composedMixin, valueDefMixin],
  cname: '合同服务内容拆分表',
  icon: 'biaoge',
  corder: 15,
  initModel(e) {
    e.config = {
      amountKey: '',
    };
  },
  properties: [{
    label: '金额字段',
    key: 'amountKey',
    control: 'Text',
    option: {
      placeholder: '如 amount，即 model / db 上的合同总额字段',
    },
  }],
  props: ['config', 'value', 'editable', 'label', 'required', 'model', 'db'],
  data() {
    return {
      items: cloneDeep(Array.isArray(this.value) ? this.value : []),
    };
  },
  computed: {
    formModel() {
      return this.model || this.db || {};
    },
    amountKeyTrimmed() {
      const c = this.config || {};
      return String(c.amountKey || c.amountField || '').trim();
    },
    /** db / model 上配置的金额字段原始值 */
    boundAmountRaw() {
      const key = this.amountKeyTrimmed;
      if (!key || !this.formModel) {
        return undefined;
      }
      return this.formModel[key];
    },
    /** 已配置字段 key，且 db[key] 有有效数值时才做拆分比例校验与展示 */
    shouldValidateSplit() {
      const key = this.amountKeyTrimmed;
      if (!key) {
        return false;
      }
      const raw = this.boundAmountRaw;
      if (raw === undefined) {
        return false;
      }
      if (raw === null || raw === '') {
        return false;
      }
      const n = Number(raw);
      return !isNaN(n);
    },
    /** 合同总额，仅 shouldValidateSplit 为 true 时可信 */
    numericContractTotal() {
      if (!this.shouldValidateSplit) {
        return 0;
      }
      return Number(this.boundAmountRaw);
    },
    /** 各拆分行「金额」之和 */
    splitSum() {
      return this.items.reduce((acc, row) => acc + (Number(row.amount) || 0), 0);
    },
    /** 已拆分金额 / 合同总额，合同总额为 0 时为 null */
    splitRatio() {
      const t = this.numericContractTotal;
      if (!this.shouldValidateSplit || t <= 0) {
        return null;
      }
      return this.splitSum / t;
    },
    splitRatioDisplay() {
      const r = this.splitRatio;
      if (r == null) {
        return '—';
      }
      return `${(r * 100).toFixed(2)}%`;
    },
    showSplitCheck() {
      return this.items.length > 0 && this.shouldValidateSplit;
    },
    presetColumns() {
      return [{
        control: 'drag',
        name: '拖',
        span: 1,
      }, {
        control: 'index',
        name: '序',
        span: 1,
      }, {
        name: '服务类型',
        key: 'business_type',
        control: 'BaseSelect',
        span: 5,
        props: {
          transfer: true,
          clearable: true,
          placeholder: '请选择',
          options: BUSINESS_TYPES,
          valueType: 'name',
          size: 'small',
        },
      }, {
        name: '金额',
        key: 'amount',
        control: 'InputNumber',
        span: 4,
        props: {
          min: 0,
          size: 'small',
        },
      }, {
        name: '类型',
        key: 'type',
        control: 'BaseSelect',
        span: 3,
        props: {
          transfer: true,
          clearable: true,
          placeholder: '请选择',
          options: SPLIT_TYPES,
          valueType: 'name',
          size: 'small',
        },
      }, {
        name: '扣除金额',
        key: 'deduct_amount',
        control: 'InputNumber',
        span: 4,
        option: {
          min: 0,
          size: 'small',
        },
      }, {
        name: '对方开票',
        key: 'unbilling',
        control: 'i-switch',
        span: 3,
        props: {
          size: 'small',
        },
      }];
    },
    checkResult() {
      const total = this.numericContractTotal;
      const sum = this.splitSum;
      const ratioStr = this.splitRatioDisplay;
      if (total <= 0) {
        return { type: 'info', message: '合同总额为 0 时不计算拆分比例' };
      }
      if (sum > total + 0.001) {
        return { type: 'error', message: `已拆分金额大于合同总额，拆分比例为 ${ratioStr}，超过 100%` };
      }
      if (sum < total - 0.001) {
        return { type: 'error', message: `拆分比例 ${ratioStr}，未满 100%，拆分不完整（已拆分 ${sum}，合同总额 ${total}）` };
      }
      if (this.items.some((r) => !r.business_type)) {
        return { type: 'error', message: '存在未选择服务类型的行，请补全或删除空行' };
      }
      return { type: 'success', message: `拆分比例 ${ratioStr}，已与合同总额对齐，拆分完整` };
    },
  },
  watch: {
    value: {
      handler(v) {
        if (Array.isArray(v)) {
          this.items = cloneDeep(v);
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    getValueDef() {
      return this.valueDefContractSplitTable();
    },
    normalizeRow(row, index) {
      const base = {
        ...row,
        amount: row.amount != null ? Number(row.amount) : 0,
        type: row.type || '自营',
        deduct_amount: row.deduct_amount != null ? Number(row.deduct_amount) : 0,
        unbilling: !!row.unbilling,
      };
      if (base.id == null) {
        base.id = `css_${Date.now()}_${index}`;
      }
      return base;
    },
    handleSave() {
      this.items = this.items.map((row, i) => this.normalizeRow(row, i));
      this.$emit('input', this.items);
    },
  },
};
</script>
